import { useEffect } from 'react'
import _isEmpty from 'lodash/isEmpty'
import _isFinite from 'lodash/isFinite'
import _isUndefined from 'lodash/isUndefined'
import { type Map, type MapMouseEvent } from 'mapbox-gl'

import { type IGBPLandCoverClass } from 'types'

import * as C from '../const'

/**
 * Sets the clicked polygon as active in mapbox so it renders differently. On
 * map move/zoom, it searches for the selected polygon by UUID to update
 * active state, since the rendered feature dataset changes on move/zoom.
 */
export default function useHandlePolygonClick(
  callback: (polygonUUID: string, polygonLCC: IGBPLandCoverClass) => void,
  map: Map | undefined,
  selectedPolygonUUIDs: string[]
): void {
  useEffect((): (() => void) => {
    /**
     * Searches for the clicked feature by location and updates its active
     * status. Also updates refs for updatedSelectedFeatureActiveStatus.
     */
    const onMapClick = (event: MapMouseEvent): void => {
      if (
        _isUndefined(map) ||
        _isUndefined(event?.point) ||
        _isUndefined((map as any).style) ||
        _isUndefined(map.getLayer(C.FEATURES_LAYER_ID))
      ) {
        return
      }

      const polygons = map.queryRenderedFeatures(event.point, {
        layers: [C.FEATURES_LAYER_ID]
      })

      if (_isEmpty(polygons)) {
        return
      }

      const polygonUUID = polygons?.[0]?.properties?.feature_uuid as string
      const polygonLCC = polygons?.[0]?.properties
        ?.land_cover as IGBPLandCoverClass
      const featureID = polygons?.[0]?.id as number

      if (_isFinite(featureID)) {
        const isActive = selectedPolygonUUIDs.includes(
          polygons?.[0]?.properties?.feature_uuid as string
        )

        map.setFeatureState(
          {
            id: featureID,
            source: C.FEATURES_SOURCE_ID
          },
          { isActive: !isActive }
        )

        callback(polygonUUID, polygonLCC)
      }
    }

    /**
     * Queries all map polygons and searches for the selected polygon by UUID
     * to resolve the current polygon ID (changes on dataset change). Updates
     * active status accordingly.
     *
     * TODO: Extract
     */
    const updatedSelectedPolygonActiveStatus = (): void => {
      if (
        _isUndefined(map) ||
        _isUndefined((map as any).style) ||
        _isUndefined(map.getLayer(C.FEATURES_LAYER_ID))
      ) {
        return
      }

      const polygons = map.queryRenderedFeatures(undefined, {
        layers: [C.FEATURES_LAYER_ID]
      })

      polygons.forEach((polygon) => {
        const isActive = selectedPolygonUUIDs.includes(
          polygon.properties?.feature_uuid as string
        )

        map.setFeatureState(
          {
            id: polygon.id as number,
            source: C.FEATURES_SOURCE_ID
          },
          { isActive }
        )
      })
    }

    map?.on('click', C.FEATURES_LAYER_ID, onMapClick)
    map?.on('data', updatedSelectedPolygonActiveStatus)

    return (): void => {
      map?.off('click', C.FEATURES_LAYER_ID, onMapClick)
      map?.off('data', updatedSelectedPolygonActiveStatus)
    }
  }, [map, (map as any)?.style, callback, selectedPolygonUUIDs])
}
